const GAMIFICATION_FLOW_STORAGE_KEY = "gamification_flow_state";

function toPositiveNumber(value, fallback = 0) {
  const nextValue = Number(value);
  return Number.isFinite(nextValue) && nextValue > 0 ? nextValue : fallback;
}

function cloneIfObject(value) {
  return value && typeof value === "object" ? { ...value } : null;
}

function cloneArray(value) {
  return Array.isArray(value) ? [...value] : [];
}

function hasOwnValue(source, key) {
  return Boolean(source) && Object.prototype.hasOwnProperty.call(source, key);
}

function pickValue(routeState, fallbackState, key, defaultValue) {
  if (hasOwnValue(routeState, key)) {
    return routeState[key];
  }

  if (hasOwnValue(fallbackState, key)) {
    return fallbackState[key];
  }

  return defaultValue;
}

function normalizeBackTarget(value) {
  if (!value?.pathname) {
    return null;
  }

  return {
    pathname: value.pathname,
    state: cloneIfObject(value.state) || {},
  };
}

function normalizeSelectedEntity(routeValue, fallbackValue, idKey, fallbackId) {
  const entity = cloneIfObject(routeValue) || cloneIfObject(fallbackValue) || null;
  if (entity) {
    return entity;
  }

  if (!fallbackId) {
    return null;
  }

  return {
    [idKey]: fallbackId,
  };
}

function toNormalizedUsersList(value) {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map((item) => ({
    ...item,
    user_id: Number(item?.user_id || 0),
    player_id: Number(item?.player_id || item?.id || 0),
  }));
}

export function getStoredGamificationFlowState() {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const rawValue = window.sessionStorage.getItem(GAMIFICATION_FLOW_STORAGE_KEY);
    const storedState = rawValue ? JSON.parse(rawValue) : {};
    return storedState?.room_request_pending
      ? { ...storedState, room_request_pending: false, room_request_action: "",
          socketError: "Previous room request was not confirmed. Check the room before trying again." }
      : storedState;
  } catch (_error) {
    return {};
  }
}

export function clearStoredGamificationFlowState() {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.removeItem(GAMIFICATION_FLOW_STORAGE_KEY);
  } catch (_error) {
    // Ignore storage failures.
  }
}

export function buildGamificationFlowState(routeState = {}, fallbackState = {}) {
  const mergedState = {
    ...fallbackState,
    ...routeState,
  };

  const stateId = toPositiveNumber(
    mergedState?.selectedState?.state_id ||
      mergedState?.state_id ||
      fallbackState?.selectedState?.state_id ||
      fallbackState?.state_id,
    13
  );
  const categoryId = toPositiveNumber(
    mergedState?.selectedCategory?.category_id ||
      mergedState?.category_id ||
      fallbackState?.selectedCategory?.category_id ||
      fallbackState?.category_id,
    6
  );
  const subjectId = toPositiveNumber(
    mergedState?.selectedSubject?.subject_id ||
      mergedState?.selectedSubject?.id ||
      mergedState?.subject_id ||
      fallbackState?.selectedSubject?.subject_id ||
      fallbackState?.selectedSubject?.id ||
      fallbackState?.subject_id,
    0
  );
  const battleId = toPositiveNumber(
    mergedState?.selectedBattle?.battles_id ||
      mergedState?.selectedBattle?.battle_id ||
      mergedState?.selectedBattle?.id ||
      mergedState?.battles_id ||
      fallbackState?.selectedBattle?.battles_id ||
      fallbackState?.selectedBattle?.battle_id ||
      fallbackState?.selectedBattle?.id ||
      fallbackState?.battles_id,
    0
  );

  const selectedState = normalizeSelectedEntity(
    mergedState?.selectedState,
    fallbackState?.selectedState,
    "state_id",
    stateId
  );
  const selectedCategory = normalizeSelectedEntity(
    mergedState?.selectedCategory,
    fallbackState?.selectedCategory,
    "category_id",
    categoryId
  );
  const selectedSubject = normalizeSelectedEntity(
    mergedState?.selectedSubject,
    fallbackState?.selectedSubject,
    "subject_id",
    subjectId
  );
  const selectedBattle = normalizeSelectedEntity(
    mergedState?.selectedBattle,
    fallbackState?.selectedBattle,
    "battles_id",
    battleId
  );

  return {
    ...fallbackState,
    ...routeState,
    selectedState,
    selectedCategory,
    selectedSubject,
    selectedBattle,
    state_id: stateId,
    category_id: categoryId,
    subject_id: subjectId,
    battles_id: battleId,
    battle_room_id: toPositiveNumber(
      pickValue(routeState, fallbackState, "battle_room_id", 0),
      0
    ),
    temp_room_id: toPositiveNumber(
      pickValue(routeState, fallbackState, "temp_room_id", 0),
      0
    ),
    room_code: pickValue(routeState, fallbackState, "room_code", "") || "",
    player_id: toPositiveNumber(pickValue(routeState, fallbackState, "player_id", 0), 0),
    userCoins: Number(
      mergedState?.userCoins ??
        mergedState?.user_coins ??
        mergedState?.user_current_coins ??
        fallbackState?.userCoins ??
        fallbackState?.user_coins ??
        fallbackState?.user_current_coins ??
        0
    ),
    user_coins: Number(
      mergedState?.user_coins ??
        mergedState?.user_current_coins ??
        mergedState?.userCoins ??
        fallbackState?.user_coins ??
        fallbackState?.user_current_coins ??
        fallbackState?.userCoins ??
        0
    ),
    waiting_time: Number(pickValue(routeState, fallbackState, "waiting_time", 0) ?? 0),
    game_waiting_time: Number(pickValue(routeState, fallbackState, "game_waiting_time", 0) ?? 0),
    group_user_limit: Number(pickValue(routeState, fallbackState, "group_user_limit", 0) ?? 0),
    entryMode: pickValue(routeState, fallbackState, "entryMode", "") || "",
    liveStage: pickValue(routeState, fallbackState, "liveStage", "") || "",
    isRoomCreator: Boolean(
      mergedState?.isRoomCreator ??
        mergedState?.is_room_creator ??
        mergedState?.is_creator ??
        fallbackState?.isRoomCreator ??
        false
    ),
    isSocketConnected: Boolean(
      mergedState?.isSocketConnected ?? fallbackState?.isSocketConnected ?? false
    ),
    currentQuestion: pickValue(routeState, fallbackState, "currentQuestion", null),
    answerSaveResponse: pickValue(routeState, fallbackState, "answerSaveResponse", null),
    result_snapshot: mergedState?.result_snapshot || fallbackState?.result_snapshot || null,
    rank: Number(mergedState?.rank ?? fallbackState?.rank ?? 0),
    score: Number(mergedState?.score ?? fallbackState?.score ?? 0),
    question_id: toPositiveNumber(
      hasOwnValue(routeState, "question_id")
        ? routeState?.question_id
        : mergedState?.currentQuestion?.question_id ??
            fallbackState?.question_id ??
            fallbackState?.currentQuestion?.question_id,
      0
    ),
    question_index: Number(
      mergedState?.question_index ??
        mergedState?.currentQuestion?.question_index ??
        fallbackState?.question_index ??
        fallbackState?.currentQuestion?.question_index ??
        0
    ),
    total_questions: Number(
      mergedState?.total_questions ??
        mergedState?.currentQuestion?.total_questions ??
        fallbackState?.total_questions ??
        fallbackState?.currentQuestion?.total_questions ??
        0
    ),
    remaining_coins: Number(
      mergedState?.remaining_coins ??
        mergedState?.currentQuestion?.remaining_coins ??
        fallbackState?.remaining_coins ??
        fallbackState?.currentQuestion?.remaining_coins ??
        0
    ),
    selected_lang:
      mergedState?.selected_lang ||
      mergedState?.currentQuestion?.selected_lang ||
      fallbackState?.selected_lang ||
      fallbackState?.currentQuestion?.selected_lang ||
      "EN",
    users_list: toNormalizedUsersList(mergedState?.users_list || fallbackState?.users_list),
    queue_users: toNormalizedUsersList(mergedState?.queue_users || fallbackState?.queue_users),
    power_up_state: pickValue(routeState, fallbackState, "power_up_state", null),
    liveScore: mergedState?.liveScore || fallbackState?.liveScore || null,
    start_confirmation_required: Boolean(
      mergedState?.start_confirmation_required ??
        fallbackState?.start_confirmation_required ??
        false
    ),
    start_countdown_seconds: Number(
      mergedState?.start_countdown_seconds ??
        fallbackState?.start_countdown_seconds ??
        0
    ),
    start_requested_at: pickValue(routeState, fallbackState, "start_requested_at", "") || "",
    confirmation_started_at: Number(pickValue(routeState, fallbackState, "confirmation_started_at", 0) || 0),
    start_response_received: Boolean(pickValue(routeState, fallbackState, "start_response_received", false)),
    matchFailureMessage: pickValue(routeState, fallbackState, "matchFailureMessage", "") || "",
    start_confirmed: Boolean(
      mergedState?.start_confirmed ?? fallbackState?.start_confirmed ?? false
    ),
    resume_token: pickValue(routeState, fallbackState, "resume_token", "") || "",
    creator_subject_name: pickValue(routeState, fallbackState, "creator_subject_name", "") || "",
    creator_state_name: pickValue(routeState, fallbackState, "creator_state_name", "") || "",
    game_entry_fee: Number(
      mergedState?.game_entry_fee ?? fallbackState?.game_entry_fee ?? 0
    ),
    gamificationBackTarget: normalizeBackTarget(
      mergedState?.gamificationBackTarget || fallbackState?.gamificationBackTarget
    ),
    battleLeaderboardTarget:
      mergedState?.battleLeaderboardTarget ||
      fallbackState?.battleLeaderboardTarget ||
      null,
    lastSocketAction: pickValue(routeState, fallbackState, "lastSocketAction", "") || "",
    socketError: pickValue(routeState, fallbackState, "socketError", "") || "",
    ignoredQuestionIds: cloneArray(
      mergedState?.ignoredQuestionIds || fallbackState?.ignoredQuestionIds
    ),
  };
}

export function persistGamificationFlowState(flowState) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(
      GAMIFICATION_FLOW_STORAGE_KEY,
      JSON.stringify(buildGamificationFlowState(flowState))
    );
  } catch (_error) {
    // Ignore storage failures and fall back to route state.
  }
}

export function buildGamificationBackTarget(pathname, state = {}) {
  return {
    pathname,
    state: buildGamificationFlowState({ ...state, gamificationBackTarget: null }),
  };
}

export function buildGamificationSubjectsState(flowState = {}) {
  const resolvedFlowState = buildGamificationFlowState(flowState);

  return {
    ...resolvedFlowState,
    selectedState: resolvedFlowState.selectedState || {
      state_id: resolvedFlowState.state_id,
    },
    selectedCategory: resolvedFlowState.selectedCategory || {
      category_id: resolvedFlowState.category_id,
    },
    selectedBattle: resolvedFlowState.selectedBattle || null,
    state_id: resolvedFlowState.state_id,
    category_id: resolvedFlowState.category_id,
    subject_id: resolvedFlowState.subject_id,
    battles_id: resolvedFlowState.battles_id,
  };
}



